"use client";

import { type Slot } from "@/components/calendar/slot-visit/types";
import CallbackRequestModal from "@/components/sup/callback-request-modal";
import { MakeOfferModal } from "@/components/sup/make-offer-modal";
import { VisitSchedulingOverlay } from "@/components/visit-scheduling/visit-scheduling-overlay";
import { useComparisonStoreData } from "@/hooks/global-multiple";
import { useAuth } from "@/hooks/use-auth";
import { useBuyerActions } from "@/hooks/use-buyer-actions";
import { BuyerRequestContext } from "@/hooks/use-buyer-request-data";
import { useGuardedAction } from "@/hooks/use-guarded-action";
import { useLoginDialog } from "@/hooks/use-login-dialog";
import { useBuyerPropertiesStore } from "@/stores/buyer/buyer-properties-store";
import { useBuyerHelpers } from "@/utils/buyer/helpers";
import { logger } from "@/utils/logger";
import { ExternalLink, GitCompareArrows, Heart, IndianRupee, MoreHorizontal, Phone } from "lucide-react";
import { useCallback, useContext, useState } from "react";
import { toast } from "sonner";
import { RequestVisitButton } from "./buttons";
interface CustomerCareWidgetProps {
  href: string;
  projectId?: string;
  unitId?: string;
  isInModal?: boolean;
  classes?: string;
  askingPrice?: number;
}
export function CustomerCareWidget({
  href,
  projectId,
  unitId,
  isInModal,
  classes,
  askingPrice
}: CustomerCareWidgetProps) {
  // --- State and Context ---
  const {
    isAuthenticated,
    user,
    refetchUser
  } = useAuth();
  const {
    openDialog
  } = useLoginDialog();
  const {
    data,
    refetch,
    actions,
    setActions
  } = useContext(BuyerRequestContext) || {};
  const {
    isPropertyShortlisted
  } = useBuyerPropertiesStore();

  // Use centralized buyer actions hook
  const {
    handleShortlist,
    handleRemoveShortlist,
    handleScheduleVisit,
    isShortlistLoading
  } = useBuyerActions();

  // Use buyer helpers
  const {
    getBuyerGroupId: getBuyerGroupIdHelper,
    createBuyerGroup
  } = useBuyerHelpers(user, refetchUser);

  // Comparison tray integration
  const comparisonTray = useComparisonStoreData(state => state.comparisonTray);
  const toggleComparison = useComparisonStoreData(state_0 => state_0.toggleComparison);
  const propertyForComparison = {
    _id: unitId ?? projectId ?? "",
    type: unitId ? "unit" : "sp"
  } as const;
  const isInComparison = comparisonTray?.some(f => f._id === propertyForComparison._id) || false;
  const [callbackModalOpen, setCallbackModalOpen] = useState(false);
  const [isVisitSchedulingOpen, setIsVisitSchedulingOpen] = useState(false);
  const [makeOfferModalOpen, setMakeOfferModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // --- Derived State ---
  const isC2cVisitRequested = data?.projectVisitC2c?.find(({
    project: p
  }) => p.id === projectId);
  const pdValues = unitId ? data?.buyerUnits?.find(({
    unit: u
  }) => u._id === unitId) : data?.buyerProjects?.find(({
    project: p_0
  }) => p_0.id === projectId);

  // Get visit request state directly from central store
  const isVisitRequested = user && actions?.requestVisitUnits.includes(unitId ?? "") || isC2cVisitRequested || pdValues?.processDetails?.[2]?.status === "requested";
  const isConfirmedVisit = pdValues?.processDetails?.[2]?.status === "confirmed";

  // Use Zustand store as primary source of truth for shortlist status
  const propertyId = unitId ?? projectId;
  const isShortlisted = Boolean(propertyId && isPropertyShortlisted(propertyId));
  const visitTitle = isVisitRequested || actions?.requestVisitUnits.includes(unitId ?? "") ? "Reschedule Visit" : "Request Visit";

  // --- Helpers ---
  const showErrorToast = (message: string, description?: string) => {
    toast.error(message, {
      description: description || "Please try again later",
      duration: 5000,
      position: "bottom-right"
    });
  };

  // Callback request handled inside modal

  // --- Shortlist Handler Logic (without guard) ---
  const performShortlistAction = useCallback(async () => {
    const propertyId_0 = unitId ?? projectId;
    if (!propertyId_0) return;
    let buyerGroupId = getBuyerGroupIdHelper();
    if (!buyerGroupId) {
      buyerGroupId = await createBuyerGroup();
    }
    if (!buyerGroupId) return;
    try {
      // Create property object based on whether we have unitId or projectId
      const property = unitId ? {
        __typename: "BuyerUnit",
        id: unitId,
        unit: {
          id: unitId
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any : {
        __typename: "BuyerProject",
        id: projectId ?? "",
        project: {
          id: projectId ?? ""
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any;
      if (isShortlisted) {
        // Use centralized remove shortlist
        await handleRemoveShortlist({
          property,
          source: "customer_care_widget"
        });
      } else {
        // Use centralized shortlist
        await handleShortlist(property, "customer_care_widget");
      }

      // Legacy context update for backward compatibility
      try {
        refetch && refetch();
      } catch (error) {
        logger.error("Failed to refetch buyer data", error);
      }
      if (setActions) {
        setActions({
          shortlistUnits: unitId ? isShortlisted ? actions?.shortlistUnits.filter(id => id !== unitId) || [] : [...(actions?.shortlistUnits || []), unitId ?? ""] : actions?.shortlistUnits || [],
          requestVisitUnits: actions?.requestVisitUnits || [],
          shortlistProjects: projectId ? isShortlisted ? actions?.shortlistProjects.filter(id_0 => id_0 !== projectId) || [] : [...(actions?.shortlistProjects || []), projectId ?? ""] : actions?.shortlistProjects || [],
          requestVisitProjects: actions?.requestVisitProjects || []
        });
      }
    } catch (e) {
      // Error handling is done in useBuyerActions
      logger.error(e);
    }
  }, [isShortlisted, unitId, projectId, getBuyerGroupIdHelper, createBuyerGroup, handleShortlist, handleRemoveShortlist, refetch, setActions, actions?.shortlistUnits, actions?.requestVisitUnits, actions?.shortlistProjects, actions?.requestVisitProjects]);

  // Use guarded action hook
  const {
    guardedAction: hShortlist,
    GuardComponent
  } = useGuardedAction("shortlist_property", performShortlistAction);
  // --- Request Visit Handler ---
  const hRequestVisit = useCallback(async (slots: Slot[]) => {
    const propertyId_1 = unitId ?? projectId;
    if (!propertyId_1) return;
    let buyerGroupId_0 = getBuyerGroupIdHelper();
    if (!buyerGroupId_0) {
      buyerGroupId_0 = await createBuyerGroup();
    }
    if (!buyerGroupId_0) return;
    try {
      // Create property object based on type
      const property_0 = unitId ? {
        __typename: "BuyerUnit",
        id: unitId,
        unit: {
          id: unitId
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any : {
        __typename: "BuyerProject",
        id: projectId ?? "",
        project: {
          id: projectId ?? ""
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any;

      // Format slots for the mutation
      const formattedSlots = slots.map(({
        start,
        end
      }) => ({
        start: typeof start === "string" ? start : start.toISOString(),
        end: typeof end === "string" ? end : end.toISOString()
      }));

      // Use centralized schedule visit
      await handleScheduleVisit(property_0, formattedSlots, "customer_care_widget");

      // Legacy context update for backward compatibility
      try {
        refetch && refetch();
      } catch (error_0) {
        logger.error("Failed to refetch buyer data", error_0);
      }
      if (setActions) {
        setActions({
          shortlistUnits: actions?.shortlistUnits || [],
          requestVisitUnits: unitId ? [...(actions?.requestVisitUnits || []), unitId ?? ""] : actions?.requestVisitUnits || [],
          shortlistProjects: actions?.shortlistProjects || [],
          requestVisitProjects: projectId ? [...(actions?.requestVisitProjects || []), projectId ?? ""] : actions?.requestVisitProjects || []
        });
      }
      return true;
    } catch (e_0) {
      logger.error(e_0);
      showErrorToast("Could not request visit");
    }
  }, [unitId, projectId, getBuyerGroupIdHelper, createBuyerGroup, handleScheduleVisit, refetch, setActions, actions]);

  // --- Wrapper for Visit Scheduling Overlay ---
  const handleScheduleComplete = useCallback(async (slots_0: {
    start: string;
    end: string;
  }[]) => {
    const convertedSlots: Slot[] = slots_0.map(slot => ({
      start: new Date(slot.start),
      end: new Date(slot.end)
    }));
    await hRequestVisit(convertedSlots);
  }, [hRequestVisit]);
  const userPhoneNumbers = user?.phones?.map(p_1 => p_1.number) || [];
  const commonRootClass = `${isInModal ? "sticky" : "fixed  px-4 pt-4 pb-12"}  ${classes ? classes : ""} bottom-0 left-0 right-0 z-50 bg-transparent  mt-auto`;
  return <>
			{/* Visit Scheduling Overlay */}
			<VisitSchedulingOverlay isOpen={isVisitSchedulingOpen} onClose={() => setIsVisitSchedulingOpen(false)} unitId={unitId} projectId={projectId} userId={user?.id} buyerId={user?.groups?.find(group => group.relationship === "buyer")?.id} propertyName={unitId ? "Unit" : "Project"} onScheduleComplete={handleScheduleComplete} />
			<div className={commonRootClass}>
				<div className={`flex items-center justify-around gap-2 lg:gap-3 max-w-md mx-auto backdrop-blur-md rounded-full shadow-lg border border-gray-200/50 px-3 lg:px-4 py-3 bg-primary-200`} title={isConfirmedVisit ? "Can not request a visit as visit appointment is already confirmed" : ""}>
					{/* Request Visit Button - Primary CTA */}
					{!isAuthenticated && <RequestVisitButton requestVisitTitle={visitTitle} onClick={() => openDialog({
          onLoginSuccess: () => {}
        })} />}
					{isAuthenticated && <RequestVisitButton requestVisitTitle={visitTitle} handleRequestVisit={() => setIsVisitSchedulingOpen(true)} isDisabled={!!isConfirmedVisit} />}

					{/* More Menu with secondary actions - simple custom menu */}
					<div className="relative">
						<button type="button" className="w-10 h-10 rounded-full border border-gray-300 bg-white hover:bg-gray-50 transition-colors flex items-center justify-center cursor-pointer outline-none focus:ring-2 focus:ring-primary-500" aria-label="More actions" onClick={e_1 => {
            e_1.stopPropagation();
            setMenuOpen(!menuOpen);
          }}>
							<MoreHorizontal className="w-5 h-5" />
						</button>

						{/* Simple menu popup */}
						{menuOpen && <div className="absolute bottom-12 right-0 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[200px] z-[10000]" onClick={e_2 => e_2.stopPropagation()}>
								{/* Shortlist */}
								<button type="button" className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 flex items-center disabled:opacity-50" disabled={!!isVisitRequested || isShortlistLoading || !!isConfirmedVisit} onClick={() => {
              setMenuOpen(false);
              if (!isAuthenticated) {
                openDialog({
                  onLoginSuccess: hShortlist
                });
              } else {
                hShortlist();
              }
            }}>
									<Heart className={`w-4 h-4 mr-3 ${isShortlisted ? "fill-primary text-primary" : ""}`} />
									{isShortlisted ? "Remove from Shortlist" : "Shortlist"}
								</button>

								{/* Compare */}
								<button type="button" className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 flex items-center" onClick={() => {
              setMenuOpen(false);
              // @ts-expect-error ignore
              toggleComparison(propertyForComparison);
            }}>
									<GitCompareArrows className={`w-4 h-4 mr-3 ${isInComparison ? "text-primary" : ""}`} />
									{isInComparison ? "Remove from Compare" : "Add to Compare"}
								</button>

								{/* Request Callback */}
								<button type="button" className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 flex items-center" onClick={() => {
              setMenuOpen(false);
              setCallbackModalOpen(true);
            }}>
									<Phone className="w-4 h-4 mr-3" />
									Request Callback
								</button>

								{/* Make Offer */}
								<button type="button" className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 flex items-center" onClick={() => {
              setMenuOpen(false);
              if (!isAuthenticated) {
                openDialog({
                  onLoginSuccess: () => setMakeOfferModalOpen(true)
                });
              } else {
                setMakeOfferModalOpen(true);
              }
            }}>
									<IndianRupee className="w-4 h-4 mr-3" />
									Make Offer
								</button>

								{/* Open in Tab - only in modal */}
								{isInModal && <>
										<div className="border-t border-gray-100 my-1" />
										<a href={href} target="_blank" rel="noopener noreferrer" className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-100 flex items-center" onClick={() => setMenuOpen(false)}>
											<ExternalLink className="w-4 h-4 mr-3" />
											Open in Tab
										</a>
									</>}
							</div>}
					</div>
				</div>
			</div>
			<CallbackRequestModal open={callbackModalOpen} onClose={() => setCallbackModalOpen(false)} isAuthenticated={isAuthenticated} userPhoneNumbers={userPhoneNumbers} projectId={projectId} unitId={unitId} />

			{/* Make Offer Modal */}
			<MakeOfferModal open={makeOfferModalOpen} onClose={() => setMakeOfferModalOpen(false)} unitId={unitId} projectId={projectId} propertyName={unitId ? "Unit" : "Project"} askingPrice={askingPrice} />

			{/* Relationship Guard Modal */}
			<GuardComponent />
		</>;
}
export default CustomerCareWidget;